import { useState, type FC, type ReactNode } from 'react';
import { useSetAtom } from 'jotai';
import {
  Button,
  IconButton,
  Tag,
  TextArea,
  ProgressBar,
  ContainedList,
  ContainedListItem,
} from '@carbon/react';
import {
  Close,
  CheckmarkOutline,
  CircleDash,
  ChevronDown,
  ChevronUp,
} from '@carbon/react/icons';
import classNames from 'classnames';
import { verificationTaskEventAtom } from './atoms';
import styles from './verification-tasks-panel.module.scss';

// One verification task. Generic: any feature with polymorphic
// VerificationItems can map records into this shape and reuse the panel.
export interface VerificationTask {
  /** Stable id (e.g. the VerificationItem id) — used for keying + posting. */
  id: string | number;
  label: string;
  description?: string;
  /** true = correct, false = incorrect, null = unverified. */
  status: boolean | null;
  /** POST target for verdict events (/verification_items/:id/events). */
  eventsHref: string;
  /** Locks the verdict controls (e.g. after promotion). */
  disabled?: boolean;
  /** Initial note text (seeds the Note field). */
  note?: string;
  /** Why the controls are locked — shown beneath a disabled task. */
  lockReason?: string | null;
}

export type VerificationVerdict = 'correct' | 'incorrect' | 'reverted';

interface VerificationTasksPanelProps {
  tasks: VerificationTask[];
  /** Tasks the app derives: no verdict or note controls, only what each task needs. */
  readOnly?: boolean;
  /** Panel header title. */
  title?: string;
  /** Category label for the contained list (e.g. "Pre-Drill Verification"). */
  groupLabel?: string;
  onClose?: () => void;
  /** Fired after a task's verdict is saved (e.g. to refetch the parent). */
  onStatusChange?: (taskId: string | number, status: boolean | null) => void;
  emptyMessage?: string;
  /** Delegates verdict posting to the consumer instead of the built-in atom. */
  onVerdict?: (taskId: string | number, verdict: VerificationVerdict) => void;
  /** When provided, a verdicted task gains a Revert action (→ unverified). */
  allowRevert?: boolean;
  /** When provided, the Note autosaves on blur via this callback. */
  onNoteSave?: (taskId: string | number, note: string) => void;
  /**
   * Counts to show instead of the ones derived from `tasks`. For a consumer whose
   * backend reports a total it does not yet expose the items for — without it the panel
   * would read "0 of 0" beside a control saying "1/4". Omit to count the rows, which is
   * what every existing consumer does.
   */
  progress?: { completed: number; total: number };
  /** Replaces the Correct / Incorrect / Unverified tag, for tasks that are computed rather than verdicted. */
  renderStatusTag?: (task: VerificationTask) => ReactNode;
}

function tagType(status: boolean | null): 'green' | 'red' | 'gray' {
  if (status === true) return 'green';
  if (status === false) return 'red';
  return 'gray';
}

function statusLabel(status: boolean | null): string {
  if (status === true) return 'Correct';
  if (status === false) return 'Incorrect';
  return 'Unverified';
}

// Generic verification-tasks drawer: progress header + task cards. Verdicts
// POST to the task's events endpoint; onStatusChange lets the parent refetch.
export const VerificationTasksPanel: FC<VerificationTasksPanelProps> = ({
  tasks,
  readOnly = false,
  title = 'Tasks',
  groupLabel = 'Verification',
  onClose,
  onStatusChange,
  emptyMessage = 'No verification tasks.',
  onVerdict,
  allowRevert = false,
  onNoteSave,
  progress,
  renderStatusTag,
}) => {
  const [expandedId, setExpandedId] = useState<string | number | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      tasks.filter((t) => t.note).map((t) => [t.id, t.note as string])
    )
  );
  const [pendingId, setPendingId] = useState<string | number | null>(null);
  const postEvent = useSetAtom(verificationTaskEventAtom);

  // Normally the counts come from the tasks themselves. A consumer whose backend reports
  // a total it cannot yet list overrides them, so the panel does not say "0 of 0" under a
  // button reading "1/4". Absent for every existing consumer, which keeps counting rows.
  const completed =
    progress?.completed ?? tasks.filter((task) => task.status !== null).length;
  const total = progress?.total ?? tasks.length;
  const pct = total === 0 ? 0 : (completed / total) * 100;

  const setVerdict = (task: VerificationTask, verdict: VerificationVerdict) => {
    // Consumer-owned posting (e.g. pre-drill's v2 event shape).
    if (onVerdict) {
      onVerdict(task.id, verdict);
      return;
    }
    if (verdict === 'reverted') return; // built-in path is binary only
    setPendingId(task.id);
    postEvent({
      type: 'POST',
      url: task.eventsHref,
      data: {
        kind:
          verdict === 'correct'
            ? 'verification_correct'
            : 'verification_incorrect',
        reason: notes[task.id] || null,
        details: null,
      },
      onSuccess: () => {
        setPendingId(null);
        onStatusChange?.(task.id, verdict === 'correct');
      },
      onError: () => setPendingId(null),
    });
  };

  const saveNote = (task: VerificationTask) => {
    const next = notes[task.id] ?? '';
    if (next === (task.note ?? '')) return; // no change
    onNoteSave?.(task.id, next);
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>
            {`${completed} of ${total} complete`}
          </p>
        </div>
        {onClose && (
          <IconButton
            kind="ghost"
            label="Hide tasks"
            align="left"
            onClick={onClose}
          >
            <Close />
          </IconButton>
        )}
      </div>

      <div className={styles.progressWrap}>
        <ProgressBar
          value={pct}
          max={100}
          hideLabel
          label="Verification progress"
          size="small"
          status={pct === 100 ? 'finished' : 'active'}
        />
      </div>

      <ContainedList
        className={styles.list}
        kind="on-page"
        size="md"
        label={
          <span className={styles.categoryLabel}>
            <span>{groupLabel}</span>
            <span className={styles.categoryCount}>
              {`${completed}/${total}`}
            </span>
          </span>
        }
      >
        {tasks.length === 0 && (
          <ContainedListItem>
            <span className={styles.empty}>{emptyMessage}</span>
          </ContainedListItem>
        )}
        {tasks.map((task) => {
          const isExpanded = expandedId === task.id;
          const ExpandIcon = isExpanded ? ChevronUp : ChevronDown;
          const StatusIcon =
            task.status === null ? CircleDash : CheckmarkOutline;
          const pending = pendingId === task.id;

          return (
            <ContainedListItem
              key={task.id}
              className={classNames(styles.task, {
                [styles.taskComplete]: task.status !== null,
                [styles.taskLocked]: task.disabled,
              })}
            >
              <div
                className={styles.taskRow}
                role="button"
                tabIndex={0}
                onClick={() => setExpandedId(isExpanded ? null : task.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedId(isExpanded ? null : task.id);
                  }
                }}
              >
                <span
                  className={classNames(styles.verify, {
                    [styles.verifyComplete]: task.status !== null,
                  })}
                  aria-hidden="true"
                >
                  <StatusIcon />
                </span>
                <div className={styles.taskInfo}>
                  <span className={styles.taskLabel}>{task.label}</span>
                  {!isExpanded && task.description && (
                    <p className={styles.taskDesc}>{task.description}</p>
                  )}
                </div>
                <div className={styles.taskMeta}>
                  {renderStatusTag ? (
                    renderStatusTag(task)
                  ) : (
                    <Tag type={tagType(task.status)} size="sm">
                      {statusLabel(task.status)}
                    </Tag>
                  )}
                  <span className={styles.expandIcon}>
                    <ExpandIcon />
                  </span>
                </div>
              </div>

              {isExpanded && (
                <div className={styles.taskExpanded}>
                  {task.description && (
                    <p className={styles.expandedDesc}>{task.description}</p>
                  )}

                  {!readOnly && (
                    <>
                      <div className={styles.verdictPicker}>
                        <span className={styles.verdictLabel}>Verdict</span>
                        <div className={styles.verdictButtons}>
                          <Button
                            kind={task.status === true ? 'primary' : 'ghost'}
                            size="sm"
                            disabled={task.disabled || pending}
                            onClick={() => setVerdict(task, 'correct')}
                          >
                            Correct
                          </Button>
                          <Button
                            kind={task.status === false ? 'danger' : 'ghost'}
                            size="sm"
                            disabled={task.disabled || pending}
                            onClick={() => setVerdict(task, 'incorrect')}
                          >
                            Incorrect
                          </Button>
                          {allowRevert && task.status !== null && (
                            <Button
                              kind="ghost"
                              size="sm"
                              disabled={task.disabled || pending}
                              onClick={() => setVerdict(task, 'reverted')}
                            >
                              Revert
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className={styles.noteSection}>
                        <TextArea
                          id={`verification-note-${task.id}`}
                          labelText="Note"
                          placeholder={`Add a note about "${task.label}"…`}
                          value={notes[task.id] ?? ''}
                          disabled={task.disabled}
                          rows={3}
                          onChange={(e) =>
                            setNotes((prev) => ({
                              ...prev,
                              [task.id]: e.target.value,
                            }))
                          }
                          onBlur={() => saveNote(task)}
                        />
                      </div>
                    </>
                  )}

                  {task.disabled && task.lockReason && (
                    <p className={styles.lockReason}>{task.lockReason}</p>
                  )}

                  <div className={styles.expandedActions}>
                    <Button
                      kind="ghost"
                      size="sm"
                      onClick={() => setExpandedId(null)}
                    >
                      Collapse
                    </Button>
                  </div>
                </div>
              )}
            </ContainedListItem>
          );
        })}
      </ContainedList>
    </div>
  );
};
